import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Alert,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

BulkAttributes.layout = (page) => <AppShell>{page}</AppShell>

export default function BulkAttributes({
  property, attribute_types, unit_attribute_matrix, change_dates = [], reference_date
}) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [rows, setRows] = useState(unit_attribute_matrix)
  const [submitting, setSubmitting] = useState(false)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10))

  const isViewingHistory = reference_date != null
  const basePath = `/m/${tenant.slug}/properties/${property.id}/units/attributes`

  // Build timeline: [...pastDates, 'current', ...futureDates]
  const today = new Date().toISOString().slice(0, 10)
  const pastDates = change_dates.filter(d => d <= today)
  const futureDates = change_dates.filter(d => d > today)
  const allPositions = [...pastDates, 'current', ...futureDates]

  const currentPosition = isViewingHistory ? reference_date : 'current'
  const currentIndex = allPositions.indexOf(currentPosition)

  const canGoBack = currentIndex > 0
  const canGoForward = currentIndex >= 0 && currentIndex < allPositions.length - 1

  const navigateTo = (position) => {
    if (position === 'current') {
      router.get(basePath, {}, { preserveState: false })
    } else {
      router.get(basePath, { reference_date: position }, { preserveState: false })
    }
  }

  const navigateBack = () => {
    if (!canGoBack) return
    navigateTo(allPositions[currentIndex - 1])
  }

  const navigateForward = () => {
    if (!canGoForward) return
    navigateTo(allPositions[currentIndex + 1])
  }

  const mea = attribute_types.find((at) => at.slug === 'mea')
  const meaTotal = mea ? rows.reduce((sum, row) => {
    const v = row.values.find((val) => {
      const at = attribute_types.find((a) => a.id === val.attribute_type_id)
      return at?.slug === 'mea'
    })?.value
    return sum + (parseFloat(v) || 0)
  }, 0) : null

  const updateValue = (unitId, atId, newValue) => {
    setRows((prev) => prev.map((row) =>
      row.unit_id === unitId
        ? { ...row, values: row.values.map((v) => v.attribute_type_id === atId ? { ...v, value: newValue } : v) }
        : row
    ))
  }

  // Check the ORIGINAL matrix loaded from the server (not the edited `rows` state)
  // so that "first-time entry" stays detected even after the user has typed values.
  const hasExistingValues = unit_attribute_matrix.some((row) =>
    row.values.some((v) => v.value != null && String(v.value).trim() !== '')
  )

  const handleSaveClick = () => {
    if (hasExistingValues) {
      setEffectiveDate(today)
    } else {
      setEffectiveDate(property.management_start || today)
    }
    setSaveDialogOpen(true)
  }

  const handleSaveConfirm = () => {
    setSaveDialogOpen(false)
    setSubmitting(true)
    router.patch(basePath, {
      effective_date: effectiveDate,
      rows,
    }, {
      onFinish: () => setSubmitting(false),
    })
  }

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return ''
    const parts = dateStr.split('-')
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`
    return dateStr
  }

  return (
    <Box>
      <PageHeader
        title="Attribute bearbeiten"
        breadcrumbs={[
          { label: 'Einheiten', href: `/m/${tenant.slug}/properties/${property.id}/units` },
          { label: 'Attribute' },
        ]}
      />

      {/* Timeline navigation widget */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, px: 2, py: 1, mb: 2,
          border: '1px solid', borderColor: 'divider',
        }}
        data-testid="timeline-widget"
      >
        <IconButton
          size="small"
          onClick={navigateBack}
          disabled={!canGoBack}
          data-testid="timeline-back"
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ minWidth: 140, textAlign: 'center' }}
          data-testid="timeline-label"
        >
          {isViewingHistory ? formatDateLabel(reference_date) : 'Aktuelle Werte'}
        </Typography>

        <IconButton
          size="small"
          onClick={navigateForward}
          disabled={!canGoForward}
          data-testid="timeline-forward"
        >
          <ChevronRightIcon />
        </IconButton>

        {change_dates.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {change_dates.length} {change_dates.length === 1 ? 'Änderung' : 'Änderungen'}
          </Typography>
        )}
      </Paper>

      {/* MEA alerts */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {mea && meaTotal !== null && meaTotal !== 1000 && (
          <Alert severity="warning" sx={{ py: 0 }}>
            MEA-Summe: {meaTotal} ‰ (Soll: 1000 ‰)
          </Alert>
        )}
        {mea && meaTotal === 1000 && (
          <Alert severity="success" sx={{ py: 0 }}>MEA-Summe: 1000 ‰ ✓</Alert>
        )}
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
              {attribute_types.map((at) => (
                <TableCell key={at.id} sx={{ fontWeight: 600 }}>
                  {at.name} {at.unit_of_measure ? `(${at.unit_of_measure})` : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.unit_id}>
                <TableCell><Typography variant="body2" fontWeight={500}>{row.unit_number}</Typography></TableCell>
                {row.values.map((val) => (
                  <TableCell
                    key={val.attribute_type_id}
                    sx={{
                      py: 0.5,
                      backgroundColor: val.changed_on_date ? 'rgba(255, 183, 77, 0.15)' : 'inherit',
                    }}
                    data-testid={val.changed_on_date ? `changed-${row.unit_id}-${val.attribute_type_id}` : undefined}
                  >
                    <TextField
                      size="small"
                      type="number"
                      value={val.value || ''}
                      onChange={(e) => updateValue(row.unit_id, val.attribute_type_id, e.target.value)}
                      disabled={isViewingHistory}
                      sx={{ width: 90 }}
                      slotProps={{ htmlInput: { step: 'any' } }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {/* Totals row */}
            {rows.length > 0 && (
              <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                <TableCell><Typography variant="body2" fontWeight={600}>Summe</Typography></TableCell>
                {attribute_types.map((at) => {
                  const total = rows.reduce((sum, row) => {
                    const v = row.values.find((val) => val.attribute_type_id === at.id)?.value
                    return sum + (parseFloat(v) || 0)
                  }, 0)
                  return (
                    <TableCell key={at.id}>
                      <Typography variant="body2" fontWeight={600}>{total || ''}</Typography>
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined"
          onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units`)}>
          {isViewingHistory ? 'Zurück' : 'Abbrechen'}
        </Button>
        {!isViewingHistory && (
          <Button variant="contained" onClick={handleSaveClick} disabled={submitting} data-testid="save-attributes-btn">
            {submitting ? 'Wird gespeichert...' : 'Speichern'}
          </Button>
        )}
      </Box>

      {/* Save effective date dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="save-dialog"
      >
        <DialogTitle>Ab wann gelten die neuen Werte?</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            type="date"
            label="Gültig ab"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ mt: 1, width: '100%' }}
            data-testid="effective-date-input"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} data-testid="save-dialog-cancel">
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleSaveConfirm} data-testid="save-dialog-confirm">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
